import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { Node, Edge, NodeMouseHandler, OnNodesChange, OnEdgesChange } from '@xyflow/react';
import type { StatusNodeData, TransitionEdgeData } from '../../hooks/usePlaybookEditor';
import { PLAYBOOK_KEYFRAMES } from '../../types/playbook.types';
import type { CourtRuleDeadline } from '../../types/playbook.types';
import { usePlaybookEditor } from '../../hooks/usePlaybookEditor';
import { useAutoLayout } from '../../hooks/useAutoLayout';
import { useEditorDiff } from '../../hooks/useEditorDiff';
import { playbookApi } from '../../api/playbookApi';
import type { AIGeneratedPlaybook, GapDetectionResult, GapItem } from '../../types/playbook.types';
import StatusNode from './StatusNode';
import TransitionEdge from './TransitionEdge';
import EditorToolbar from './EditorToolbar';
import SidebarPropertyPanel from './SidebarPropertyPanel';
import CourtRulesBrowser from './CourtRulesBrowser';
import CanvasDiffOverlay from './CanvasDiffOverlay';
import DiffSummaryBar from './DiffSummaryBar';
import DiffVersionSelector from './DiffVersionSelector';
import AIAssistantPanel from './AIAssistantPanel';
import AICanvasSuggestions, { useAIGhostElements } from './AICanvasSuggestions';
import MigrationWizard from './MigrationWizard';

const nodeTypes = { statusNode: StatusNode };
const edgeTypes = { transitionEdge: TransitionEdge };

const defaultEdgeOptions = {
  type: 'transitionEdge',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#9ca3af' },
};

interface PlaybookEditorCanvasProps {
  playbookId: string;
  slug?: string;
  canPublish: boolean;
  onPublish: () => void;
  onSimulate: () => void;
}

const EditorInner: React.FC<PlaybookEditorCanvasProps> = ({ playbookId, slug, canPublish, onPublish, onSimulate }) => {
  const editor = usePlaybookEditor(playbookId);
  const { getLayoutedElements } = useAutoLayout();
  const diffState = useEditorDiff(playbookId, slug);
  const [showCourtRules, setShowCourtRules] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showMigration, setShowMigration] = useState(false);
  const [gapResult, setGapResult] = useState<GapDetectionResult | null>(null);
  const { fitView } = useReactFlow();
  const { ghostNodes, ghostEdges } = useAIGhostElements(gapResult, editor.nodes);
  const hasAutoLaidOut = React.useRef(false);

  const handleAutoLayout = useCallback(() => {
    const { nodes: layouted, edges } = getLayoutedElements(editor.nodes, editor.edges);
    editor.setNodes(layouted as Node<StatusNodeData>[]);
    editor.setEdges(edges as Edge<TransitionEdgeData>[]);
    requestAnimationFrame(() => fitView({ padding: 0.2 }));
  }, [editor.nodes, editor.edges, getLayoutedElements, fitView, editor.setNodes, editor.setEdges]);

  // Auto-layout on first load when nodes have no saved positions (all stacked at 0,0)
  useEffect(() => {
    if (hasAutoLaidOut.current || editor.loading || editor.nodes.length < 2) return;
    const allStacked = editor.nodes.every((n) => n.position.x === 0 && n.position.y === 0);
    if (allStacked) {
      hasAutoLaidOut.current = true;
      handleAutoLayout();
    }
  }, [editor.loading, editor.nodes, handleAutoLayout]);

  const handleNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    editor.setSelectedNodeId(node.id);
    editor.setSelectedEdgeId(null);
  }, [editor]);

  const handleEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    editor.setSelectedEdgeId(edge.id);
    editor.setSelectedNodeId(null);
  }, [editor]);

  const handlePaneClick = useCallback(() => {
    editor.setSelectedNodeId(null);
    editor.setSelectedEdgeId(null);
  }, [editor]);

  // Keyboard delete
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'SELECT') return;
        if (editor.selectedNodeId) editor.deleteStatus(editor.selectedNodeId);
        else if (editor.selectedEdgeId) editor.deleteTransition(editor.selectedEdgeId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editor.selectedNodeId, editor.selectedEdgeId, editor.deleteStatus, editor.deleteTransition]);

  // Court rules → create calculate_deadline action
  const handleCourtRuleSelect = useCallback((deadline: CourtRuleDeadline) => {
    // If a transition is selected, auto-fill a calculate_deadline action
    if (editor.selectedTransition) {
      playbookApi.addAction(editor.selectedTransition.id, {
        action_type: 'calculate_deadline',
        configuration: {
          statute_reference: deadline.statute_reference,
          base_date_expression: deadline.base_event,
          direction: deadline.direction,
          day_type: deadline.day_type,
        },
        execution_order: (editor.selectedTransition.actions?.length ?? 0),
      }).then(() => editor.refreshPlaybook());
    }
    setShowCourtRules(false);
  }, [editor.selectedTransition, editor.refreshPlaybook]);

  const handleToggleDiff = useCallback(() => {
    if (diffState.diffMode) {
      diffState.exitDiffMode();
    } else {
      diffState.enterDiffMode();
    }
  }, [diffState]);

  const handlePreviewGenerated = useCallback((_generated: AIGeneratedPlaybook) => {
    // Preview is shown inline in the AI panel
  }, []);

  const handleGapsDetected = useCallback((result: GapDetectionResult) => {
    setGapResult(result);
  }, []);

  const handleAcceptGap = useCallback((gap: GapItem) => {
    // Accept a gap suggestion — trigger a refresh to pick up changes
    if (gap.suggestedFix) {
      editor.refreshPlaybook();
    }
    setGapResult((prev) => prev ? { ...prev, gaps: prev.gaps.filter((g) => g !== gap) } : null);
  }, [editor]);

  const handleDismissGap = useCallback((index: number) => {
    setGapResult((prev) => prev ? { ...prev, gaps: prev.gaps.filter((_, i) => i !== index) } : null);
  }, []);

  const handleMigrationComplete = useCallback((_playbookId: string) => {
    setShowMigration(false);
    // Optionally navigate to the new playbook
  }, []);

  if (editor.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading editor...</span>
      </div>
    );
  }

  if (editor.error && !editor.playbook) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {editor.error}
      </div>
    );
  }

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>
      <div className="flex flex-col h-[calc(100vh-200px)] min-h-[500px]" style={{ animation: 'fadeSlideIn 300ms ease-out' }}>
        {/* Toolbar */}
        <EditorToolbar
          dirty={editor.dirty}
          saving={editor.saving}
          drawingEdge={editor.drawingEdge}
          diffMode={diffState.diffMode}
          onAddStatus={() => editor.addStatus()}
          onToggleDrawEdge={() => editor.setDrawingEdge(!editor.drawingEdge)}
          onAutoLayout={handleAutoLayout}
          onSave={editor.saveLayout}
          onPublish={onPublish}
          onSimulate={onSimulate}
          onToggleDiff={handleToggleDiff}
          onToggleCourtRules={() => setShowCourtRules(!showCourtRules)}
          showCourtRules={showCourtRules}
          canPublish={canPublish}
          onToggleAI={() => { setShowAI(!showAI); if (showCourtRules) setShowCourtRules(false); }}
          showAI={showAI}
          onOpenMigration={() => setShowMigration(true)}
        />

        {/* Diff summary bar */}
        {diffState.diffMode && diffState.diff && (
          <DiffSummaryBar diff={diffState.diff} onExit={diffState.exitDiffMode} />
        )}

        {/* Diff version selector */}
        {diffState.diffMode && !diffState.diff && (
          <DiffVersionSelector
            versions={diffState.versions}
            baseVersionId={diffState.baseVersionId}
            loading={diffState.loading}
            onSelectVersion={diffState.loadDiff}
          />
        )}

        {/* Error banner */}
        {editor.error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200 flex items-center justify-between">
            <span>{editor.error}</span>
            <button type="button" onClick={() => editor.setError(null)} className="text-rose-400 hover:text-rose-600">×</button>
          </div>
        )}

        {/* Main content */}
        <div className="flex flex-1 mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Canvas */}
          <div className="flex-1 relative">
            {diffState.diffMode && diffState.diff ? (
              <CanvasDiffOverlay
                nodes={editor.nodes}
                edges={editor.edges}
                diff={diffState.diff}
              />
            ) : (
              <ReactFlow
                nodes={[...editor.nodes, ...ghostNodes]}
                edges={[...editor.edges, ...ghostEdges]}
                onNodesChange={editor.onNodesChange as OnNodesChange<Node>}
                onEdgesChange={editor.onEdgesChange as OnEdgesChange<Edge>}
                onConnect={editor.onConnect}
                onNodeClick={handleNodeClick}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.2}
                maxZoom={2}
                snapToGrid
                snapGrid={[16, 16]}
              >
                <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e5e7eb" />
                <MiniMap
                  nodeStrokeWidth={3}
                  zoomable
                  pannable
                  className="!bg-gray-50 dark:!bg-gray-900 !border-gray-200 dark:!border-gray-700"
                />
                <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !shadow-sm" />
              </ReactFlow>
            )}

            {/* AI gap suggestion cards overlay */}
            {!diffState.diffMode && gapResult && (
              <AICanvasSuggestions
                gapResult={gapResult}
                nodes={editor.nodes}
                edges={editor.edges}
                onAcceptGap={handleAcceptGap}
                onDismissGap={handleDismissGap}
              />
            )}
          </div>

          {/* Right sidebar */}
          {!diffState.diffMode && (
            <div className="w-72 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
              <SidebarPropertyPanel
                selectedStatus={editor.selectedStatus}
                selectedTransition={editor.selectedTransition}
                statuses={editor.playbook?.statuses ?? []}
                onUpdateStatus={editor.updateStatus}
                onDeleteStatus={editor.deleteStatus}
                onUpdateTransition={editor.updateTransition}
                onDeleteTransition={editor.deleteTransition}
                onRefresh={editor.refreshPlaybook}
              />
            </div>
          )}

          {/* Court Rules sidebar */}
          {showCourtRules && !diffState.diffMode && (
            <CourtRulesBrowser
              onSelectDeadline={handleCourtRuleSelect}
              onClose={() => setShowCourtRules(false)}
            />
          )}

          {/* AI Assistant sidebar */}
          {showAI && !diffState.diffMode && (
            <AIAssistantPanel
              playbookId={playbookId}
              onPreviewGenerated={handlePreviewGenerated}
              onGapsDetected={handleGapsDetected}
              onClose={() => setShowAI(false)}
            />
          )}
        </div>
      </div>

      {/* Migration Wizard modal */}
      {showMigration && (
        <MigrationWizard
          onComplete={handleMigrationComplete}
          onClose={() => setShowMigration(false)}
        />
      )}
    </>
  );
};

const PlaybookEditorCanvas: React.FC<PlaybookEditorCanvasProps> = (props) => (
  <ReactFlowProvider>
    <EditorInner {...props} />
  </ReactFlowProvider>
);

export default PlaybookEditorCanvas;
